import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, FileText, Download, X as XIcon } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["New", "Open", "Fixed", "Ready for test", "Rejected", "Cancelled", "Done"];

const IMG_EXT = /\.(png|jpe?g|gif|webp|bmp|svg|heic|heif)(\?|$)/i;
const VID_EXT = /\.(mp4|webm|mov|m4v|ogv|ogg)(\?|$)/i;
const attMime = (a) => a.mime || a.type || "";
const attSrc = (a) => {
  if (a.url) return a.url.startsWith("http") ? a.url : `${API.replace(/\/api$/, "")}${a.url}`;
  if (a.id) return `${API}/files/${a.id}`;
  return "";
};
const isImage = (a) => attMime(a).startsWith("image/") || IMG_EXT.test(a.url || "") || IMG_EXT.test(a.name || "");
const isVideo = (a) => attMime(a).startsWith("video/") || VID_EXT.test(a.url || "") || VID_EXT.test(a.name || "");

export default function IssueDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [i, setI] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [notes, setNotes] = useState("");
  const [lightbox, setLightbox] = useState(null); // attachment being previewed fullscreen

  const load = async () => {
    const { data } = await api.get(`/issues/${id}`);
    setI(data); setNewStatus(data.status); setNotes(data.reviewer_notes || "");
  };
  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);
  if (!i) return <AppLayout title="Loading..."><div>Loading…</div></AppLayout>;

  const canReview = user.role === "admin" || user.role === "manager";

  const save = async () => {
    try {
      await api.patch(`/issues/${id}`, { status: newStatus, reviewer_notes: notes });
      toast.success("Updated");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <AppLayout title={i.title} subtitle={`BUG-${i.id.slice(0,6).toUpperCase()}`}>
      <button onClick={()=>nav("/issues")} className="text-sm text-slate-500 hover:text-slate-900 mb-3 flex items-center gap-1.5" data-testid="btn-back-issues"><ArrowLeft className="w-4 h-4"/> Issues</button>

      {i.suspected_duplicate_of && (
        <div className="mb-4 border border-amber-300 bg-amber-50 rounded-md p-4 flex items-start gap-3" data-testid="dup-banner">
          <div className="w-2 h-2 mt-1.5 rounded-full bg-amber-500"/>
          <div className="flex-1">
            <div className="text-sm font-semibold text-amber-900">
              Possible duplicate of <button onClick={()=>nav(`/issues/${i.suspected_duplicate_of}`)} className="underline hover:text-amber-700 font-mono" data-testid="dup-banner-link">BUG-{i.suspected_duplicate_of.slice(0,6).toUpperCase()}</button>
              <span className="ml-2 text-[11px] font-normal text-amber-800">({Math.round((i.dedup_confidence||0)*100)}% confidence — auto-flagged by triage AI)</span>
            </div>
            {i.dedup_reason && <div className="text-xs text-amber-800 mt-1">{i.dedup_reason}</div>}
          </div>
          {canReview && (
            <Button size="sm" variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-100" onClick={async()=>{
              try { await api.patch(`/issues/${id}`, { status: "Duplicate" }); toast.success("Marked as Duplicate"); load(); }
              catch { toast.error("Failed"); }
            }} data-testid="btn-mark-duplicate">Mark as Duplicate</Button>
          )}
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 kpi-card">
          <div className="flex items-center gap-2 mb-3">
            <StatusBadge kind="severity" value={i.severity}/>
            <StatusBadge kind="priority" value={i.priority}/>
            <StatusBadge kind="issue" value={i.status}/>
          </div>
          <h2 className="font-display text-xl font-bold text-slate-900">{i.title}</h2>
          <div className="text-xs text-slate-500 mt-1">Reported by {i.reporter_name || "—"} · {new Date(i.created_at).toLocaleString()}</div>

          <div className="grid md:grid-cols-2 gap-4 mt-5">
            <div>
              <div className="text-[11px] uppercase text-slate-500">Steps to reproduce</div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap mt-1">{i.steps_to_reproduce || "—"}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-slate-500">Environment / device</div>
              <div className="text-sm text-slate-800 mt-1">{i.environment || "—"}<br/>{i.device_browser || "—"}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-slate-500">Expected</div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap mt-1">{i.expected_result || "—"}</div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-slate-500">Actual</div>
              <div className="text-sm text-slate-800 whitespace-pre-wrap mt-1">{i.actual_result || "—"}</div>
            </div>
          </div>

          {i.attachments?.length > 0 && (
            <div className="mt-5">
              <div className="text-[11px] uppercase text-slate-500 mb-2">Attachments</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {i.attachments.map((a, idx) => {
                  const src = attSrc(a);
                  const img = isImage(a);
                  const vid = isVideo(a);
                  if (img) {
                    return (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setLightbox(a)}
                        className="group relative block w-full aspect-video rounded-md overflow-hidden border border-slate-200 bg-slate-50 hover:border-cyan-500 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        data-testid={`attachment-${idx}`}
                        title={a.name}
                      >
                        <img src={src} alt={a.name} loading="lazy" className="w-full h-full object-cover"/>
                        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 text-[11px] text-white truncate text-left">{a.name}</div>
                      </button>
                    );
                  }
                  if (vid) {
                    return (
                      <div key={idx} className="rounded-md overflow-hidden border border-slate-200 bg-slate-950" data-testid={`attachment-${idx}`}>
                        <video
                          src={src}
                          controls
                          preload="metadata"
                          className="w-full aspect-video bg-black cursor-pointer"
                          onClick={(e) => { e.preventDefault(); setLightbox(a); }}
                        />
                        <div className="px-2 py-1.5 text-[11px] text-slate-300 truncate flex items-center justify-between gap-2 bg-slate-900">
                          <span className="truncate" title={a.name}>{a.name}</span>
                          <a href={src} download={a.name} className="text-cyan-300 hover:text-cyan-100 inline-flex items-center gap-1 shrink-0" data-testid={`attachment-download-${idx}`}><Download className="w-3 h-3"/></a>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <a key={idx} href={src} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-cyan-700 hover:underline border border-slate-200 rounded-md p-2 bg-white self-start" data-testid={`attachment-${idx}`}>
                      <FileText className="w-4 h-4"/> <span className="truncate">{a.name}</span>
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {canReview && (
          <div className="kpi-card">
            <h3 className="font-display font-bold text-slate-900 mb-3">Triage</h3>
            <Label>Status</Label>
            <Select value={newStatus} onValueChange={setNewStatus}>
              <SelectTrigger data-testid="select-issue-status" className="mt-1"><SelectValue/></SelectTrigger>
              <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <Label className="mt-3 block">Reviewer notes</Label>
            <Textarea rows={4} value={notes} onChange={e=>setNotes(e.target.value)} className="mt-1" data-testid="input-issue-notes"/>
            <Button onClick={save} className="w-full mt-3 bg-slate-900 hover:bg-slate-800" data-testid="btn-save-triage">Save</Button>
          </div>
        )}
      </div>

      {lightbox && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="lightbox-overlay"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 rounded-full bg-white/10 hover:bg-white/20 text-white p-2"
            aria-label="Close preview"
            data-testid="lightbox-close"
          >
            <XIcon className="w-5 h-5"/>
          </button>
          <a
            href={attSrc(lightbox)}
            download={lightbox.name}
            onClick={(e) => e.stopPropagation()}
            className="absolute top-4 right-16 rounded-full bg-white/10 hover:bg-white/20 text-white p-2 inline-flex items-center gap-1.5 text-xs"
            data-testid="lightbox-download"
          >
            <Download className="w-4 h-4"/> Download
          </a>
          <div className="max-w-6xl max-h-[90vh] w-full flex flex-col items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {isVideo(lightbox) ? (
              <video src={attSrc(lightbox)} controls autoPlay className="max-h-[85vh] max-w-full rounded-md shadow-2xl bg-black" data-testid="lightbox-video"/>
            ) : (
              <img src={attSrc(lightbox)} alt={lightbox.name} className="max-h-[85vh] max-w-full object-contain rounded-md shadow-2xl" data-testid="lightbox-image"/>
            )}
            <div className="text-xs text-white/80 truncate max-w-full text-center" data-testid="lightbox-caption">{lightbox.name}</div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
